import React, { useEffect, useMemo, useState } from 'react';
import { useUsersStore } from '../../store/useUsersStore';
import TemplateModal from './TemplateModal';
import DeleteModal from '../../components/DeleteModal';


const TemplatesTable = () => {
  const users = useUsersStore((s) => s.users);
  const selectedKey = useUsersStore((s) => s.selectedKey);
  const selectedUser = useUsersStore((s) => s.getSelectedUser());
  // const skills = selectedUser?.skills || [];
  const loadTemplates = useUsersStore((s) => s.loadTemplates);
  const loadTemplateCategories = useUsersStore((s) => s.loadTemplateCategories);
  const addTemplate = useUsersStore((s) => s.addTemplate);
  const updateTemplate = useUsersStore((s) => s.updateTemplate);
  const deleteTemplate = useUsersStore((s) => s.deleteTemplate);


  const [search, setSearch] = useState('');
  const [showEntries, setShowEntries] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);


  const subUserId = useMemo(() => {
    if (!selectedKey || !Array.isArray(users)) return null;
    const found = users.find((u) => {
      const candidates = [
        u.document_id,
        u.documentId,
        u.sub_user_id,
        u.subUserId,
        u.id,
        u._id,
        u.sub_username,
        u.subUsername,
        u.username,
        u.email,
      ];
      return candidates.some((c) => c && String(c) === String(selectedKey));
    });
    return found ? String(found.document_id || found.sub_user_id || found.id) : null;
  }, [selectedKey, users]);

    const skills = useMemo(() => {
    const raw = selectedUser?.skills ?? [];
    let arr = [];
    if (Array.isArray(raw)) arr = raw;
    else if (raw && typeof raw === 'object') arr = Object.values(raw);
    else arr = [];

    return arr
      .map((s, i) => {
        if (s === null || typeof s === 'undefined') return null;
        if (typeof s === 'string') return { id: s, name: s };
        if (typeof s === 'object') {
          return { id: s.id ?? s.name ?? `skill-${i}`, name: s.name ?? s.skill ?? String(s) };
        }
        return { id: `skill-${i}`, name: String(s) };
      })
      .filter(Boolean);
  }, [selectedUser]);

  // ============ LOAD TEMPLATES & CATEGORIES ============
  useEffect(() => {
    if (!subUserId) {
      setTemplates([]);
      setCategories([]);
      return;
    }


    const load = async () => {
      setLoading(true);
      try {
        console.log('🔄 Loading templates & categories for subUserId:', subUserId);

        const [cats, tpls] = await Promise.all([
          loadTemplateCategories(subUserId),
          loadTemplates(subUserId),
        ]);


        console.log('✓ Loaded', cats.length, 'categories and', tpls.length, 'templates');
        setCategories(Array.isArray(cats) ? cats : []);
        setTemplates(Array.isArray(tpls) ? tpls : []);
      } catch (err) {
        console.error('❌ Error loading templates:', err);
        setCategories([]);
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };


    load();
  }, [subUserId, loadTemplates, loadTemplateCategories]);


  // ============ SYNC WITH STORE WHEN USER CHANGES ============
  useEffect(() => {
    if (!subUserId) return;


    const selectedUser = users.find((u) => {
      const candidates = [u.document_id, u.sub_user_id, u.id, u.sub_username];
      return candidates.some((c) => c && String(c) === String(subUserId));
    });


    if (selectedUser) {
      const cats = Array.isArray(selectedUser.template_categories) ? selectedUser.template_categories : [];
      const tpls = Array.isArray(selectedUser.templates) ? selectedUser.templates : [];
      setCategories(cats);
      setTemplates(tpls);
    }
  }, [subUserId, users]);


  // ============ RESET PAGE ON FILTER CHANGE ============
  useEffect(() => {
    setCurrentPage(1);
  }, [search, showEntries]);


  // ============ FILTER & SORT ============
  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates
      .filter((t) => {
        if (!q) return true;
        const categoryMatch = String(t.categoryName || '').toLowerCase().includes(q);
        const contentMatch = String(t.content || '').toLowerCase().includes(q);
        return categoryMatch || contentMatch;
      })
      .sort((a, b) => {
        const aOrder = a.order ?? 0;
        const bOrder = b.order ?? 0;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (b.createdAt || 0) - (a.createdAt || 0);
      });
  }, [templates, search]);


  // ============ PAGINATION ============
  const total = filteredSorted.length;
  const startIndex = total === 0 ? 0 : (currentPage - 1) * showEntries + 1;
  const endIndex = Math.min(currentPage * showEntries, total);
  const totalPages = Math.max(1, Math.ceil(total / showEntries));
  const paged = filteredSorted.slice((currentPage - 1) * showEntries, currentPage * showEntries);


  // ============ HANDLERS ============
  const openAdd = (categoryId = '') => {
    setEditing(null);
    setModalOpen(true);
  };


  const openEdit = (tpl) => {
    setEditing(tpl);
    setModalOpen(true);
  };


  const handleSubmit = async (payload) => {
    if (!subUserId) return;
    setLoading(true);
    try {
      if (editing && editing.id) {
        await updateTemplate(subUserId, editing.id, payload);
      } else {
        await addTemplate(subUserId, payload);
      }
      setModalOpen(false);
      setEditing(null);


      const updated = await loadTemplates(subUserId);
      setTemplates(Array.isArray(updated) ? updated : []);
    } catch (err) {
      console.error('❌ Save template error:', err);
      alert('Save failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };


  // Open delete modal
  const openDeleteModal = (tpl) => {
    setTemplateToDelete(tpl);
    setDeleteModalOpen(true);
  };


  // Close delete modal
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setTemplateToDelete(null);
  };


  // Confirm delete
  const confirmDelete = async () => {
    if (!subUserId || !templateToDelete) return;
    setLoading(true);
    try {
      await deleteTemplate(subUserId, templateToDelete.id);
      closeDeleteModal();


      const updated = await loadTemplates(subUserId);
      setTemplates(Array.isArray(updated) ? updated : []);
    } catch (err) {
      console.error('❌ Delete template error:', err);
      alert('Delete failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="bg-white rounded shadow p-6">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Templates</h2>
        <button 
          onClick={() => openAdd()} 
          className="px-5 py-2.5 bg-white border-[1.5px] border-primary-blue text-primary-blue font-medium font-gilroy text-sm rounded-lg hover:bg-primary-blue hover:text-white transition-colors"
          disabled={loading}
        >
          Add Template
        </button>
      </div>


      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search by category or content"
            className="w-full px-4 py-3 pr-12 border-[1.4px] border-primary-blue rounded-lg text-sm focus:outline-none ring-1 focus:ring-primary-blue transition-colors"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <svg
            className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>


      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-primary-black-shade-10">
        <table className="min-w-full">
          <thead className="bg-primary-blue">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-medium text-white font-gilroy">Category</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-white font-gilroy">Content</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-white font-gilroy">Skills</th>
              <th className="px-6 py-3 text-center text-sm font-medium text-white">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-primary-black-shade-10">
            {paged.map((t) => (
              <tr key={t.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                  {t.categoryName || 'Uncategorized'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  <div
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 400,
                    }}
                  >
                    {t.content?.length > 100 ? `${t.content.slice(0, 100)}...` : t.content}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    {t.skills || 'All skills'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-center gap-2">
                    {/* Edit Icon */}
                    <button
                      onClick={() => openEdit(t)}
                      className="p-1 hover:opacity-70 transition-opacity"
                      title="Edit"
                      disabled={loading}
                    >
                      <img
                        src="/Icons/edit-icon.svg"
                        alt="Edit"
                        className="w-6 h-6"
                      />
                    </button>


                    {/* Delete Icon - Now opens modal */}
                    <button
                      onClick={() => openDeleteModal(t)}
                      className="p-1 hover:opacity-70 transition-opacity"
                      title="Delete"
                      disabled={loading}
                    >
                      <img
                        src="/Icons/delete-icon.svg"
                        alt="Delete"
                        className="w-6 h-6"
                      />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {paged.length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                  {loading ? 'Loading...' : 'No templates found.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between text-sm text-primary-blue">
        <div>
          Showing {startIndex} to {endIndex} of {total} entries
        </div>
        <div className="flex items-center gap-2">
          {/* Previous Button */}
          <button
            className="w-10 h-10 flex items-center justify-center border-2 border-primary-blue rounded text-primary-blue disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary-blue hover:text-white transition-colors"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>


          {/* Page Numbers - Show max 3 pages */}
          {(() => {
            const pages = [];
            let startPage = Math.max(1, currentPage - 1);
            let endPage = Math.min(totalPages, startPage + 2);

            // Adjust start if we're near the end
            if (endPage - startPage < 2) {
              startPage = Math.max(1, endPage - 2);
            }

            for (let i = startPage; i <= endPage; i++) {
              pages.push(
                <button
                  key={i}
                  onClick={() => setCurrentPage(i)}
                  className={`w-10 h-10 flex items-center justify-center border-2 rounded font-medium transition-colors ${currentPage === i
                      ? 'bg-primary-blue text-white border-primary-blue'
                      : 'border-primary-blue text-primary-blue hover:bg-primary-blue hover:text-white'
                    }`}
                >
                  {i}
                </button>
              );
            }
            return pages;
          })()}

          {/* Next Button */}
          <button
            className="w-10 h-10 flex items-center justify-center border-2 border-primary-blue rounded text-primary-blue disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary-blue hover:text-white transition-colors"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Skill Stats */}
      {selectedUser && Array.isArray(skills) && skills.length > 0 && (
        <section className="mt-8 bg-white rounded shadow p-6">
          <h2 className="text-xl font-gilroy font-semibold mb-4">Skill Stats</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
            {skills.map((skill) => (
              <div
                key={skill.id ?? skill.name}
                className="border rounded-lg px-4 py-3 shadow-sm bg-light-blue border-primary-blue flex flex-col justify-center items-center"
              >
                <div className="text-sm font-semibold items-center font-gilroy text-blue-700">
                  {skill.name}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}


      {/* Modal */}
      {modalOpen && (
        <TemplateModal
          isOpen={modalOpen}
          categories={categories}
          template={editing}
          onClose={() => {
            setModalOpen(false);
            setEditing(null);
          }}
          onSave={handleSubmit}
          isLoading={loading}
        />
      )}


      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        templateName={templateToDelete ? `in category "${templateToDelete.categoryName || 'Uncategorized'}"` : 'this template'}
      />
    </div>
  );
};


export default TemplatesTable;
